/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Small modal for creating a conversation folder — mirrors the NewGroup modal
// shell (transparent Modal + backdrop + card + name input + Cancel/Create).

import { X } from "lucide-react-native";
import { useEffect, useState } from "react";
import { Modal, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { WEB_INPUT_OUTLINE_RESET } from "../foundations/webInputStyle";
import { WebPortal } from "./composer/WebPortal";

const MAX_FOLDER_NAME = 40;
// Render through the web body portal (instant — skips RN-Web Modal's two-phase
// mount gate that makes the dialog appear a frame late); RN Modal on native.
const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";

export interface CreateFolderModalProps {
  readonly open: boolean;
  readonly onDismiss: () => void;
  /** Create the folder with the (trimmed, non-empty) name. */
  readonly onCreate: (name: string) => void;
}

export function CreateFolderModal({
  open,
  onDismiss,
  onCreate,
}: CreateFolderModalProps): JSX.Element | null {
  const [name, setName] = useState("");
  useEffect(() => {
    if (!open) setName("");
  }, [open]);

  if (!open) return null;
  const trimmed = name.trim();

  const submit = (): void => {
    if (trimmed.length === 0) return;
    onCreate(trimmed);
    onDismiss();
  };

  const card = (
    <Pressable
      accessibilityRole="none"
      onPress={onDismiss}
      style={StyleSheet.absoluteFill}
      className="z-50 flex-1 items-center justify-center px-md bg-black/60"
    >
        <Pressable
          onPress={() => undefined}
          className="w-full max-w-[420px] rounded-[12px] border bg-light-surface-primary border-light-border-secondary dark:bg-[#15171C] dark:border-[#22242A] shadow-2xl"
        >
          {/* Header */}
          <View className="flex flex-row items-center justify-between px-[29px] pt-5 pb-2">
            <Text className="m-0 font-sans font-bold text-[20px] leading-7 text-light-text-primary dark:text-white">
              New folder
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close"
              onPress={onDismiss}
              className="w-[26px] h-[26px] rounded-[8px] flex items-center justify-center hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
            >
              <X size={18} strokeWidth={2} color="#B5BAC3" />
            </Pressable>
          </View>

          {/* Name input */}
          <View className="px-[29px] pt-1 pb-2 gap-1">
            <Text className="font-sans text-[14px] leading-5 font-semibold text-light-text-primary dark:text-[#F5F6F8]">
              Folder name
            </Text>
            <View className="flex flex-row items-center h-[38px] px-3 gap-sm rounded-[8px] bg-light-surface-secondary dark:bg-[#111318]">
              <TextInput
                value={name}
                onChangeText={(t) => setName(t.slice(0, MAX_FOLDER_NAME))}
                placeholder="e.g. Work, Family"
                placeholderTextColor="#6B7280"
                autoFocus
                maxLength={MAX_FOLDER_NAME}
                onSubmitEditing={submit}
                className="flex-1 font-sans text-[14px] text-light-text-primary dark:text-white"
                style={WEB_INPUT_OUTLINE_RESET}
              />
            </View>
          </View>

          {/* Footer */}
          <View className="flex flex-row items-center gap-3 px-[29px] py-4 border-t border-light-border-secondary dark:border-[#22242A]">
            <Pressable
              accessibilityRole="button"
              onPress={onDismiss}
              className="flex-1 h-9 rounded-[8px] items-center justify-center border border-light-border-secondary dark:border-[#2F333B]"
            >
              <Text className="font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ disabled: trimmed.length === 0 }}
              disabled={trimmed.length === 0}
              onPress={submit}
              className={`flex-1 h-9 rounded-[8px] items-center justify-center bg-light-accent-brand dark:bg-dark-accent-brand ${
                trimmed.length === 0 ? "opacity-40" : "active:opacity-80"
              }`}
            >
              <Text className="font-sans text-[14px] font-bold text-white">Create</Text>
            </Pressable>
          </View>
        </Pressable>
    </Pressable>
  );

  if (IS_WEB) {
    return (
      <WebPortal>
        <View style={StyleSheet.absoluteFill}>{card}</View>
      </WebPortal>
    );
  }
  return (
    <Modal visible={open} transparent animationType="none" onRequestClose={onDismiss}>
      {card}
    </Modal>
  );
}
